import React from 'react';

import { ObjectId } from 'bson';

import { StaffAllocationSlider } from '.';
import {
  staffMinutesToTime,
  addMinutesToTime,
  timeToString,
  timeToMinutes,
  stringToTime,
  staffMinutesToRelativeTime,
  relativeTimeToMinutes,
  minutesToTimeSpan,
  DEFAULT_TIMESTR_FORMAT,
} from '../../shared/lib/time';
import { StaffTemplate } from '../../shared/types/staffTemplate';

import { RelatedEventTime } from './StaffAllocationSlider';
import { PartialStaffAllocation } from './EventTemplateForm';
import { StaffAllocation } from '@go-shared/types/staffAllocation';
import { toast } from './UIkit/Toast/Toast';
import { FormGroup, Typeahead, TextButton, InlineIcon, Button } from '@spark-labs/avocado-ui';
import { TypeaheadItem } from '@spark-labs/avocado-ui/dist/components/Typeahead';
import { InputElement } from '@spark-labs/avocado-ui/dist/components/Input';

/**
 * This component can either be "absolute" or "relative". Absolute staff allocations are calculated
 * based on a fixed event start time. For example, a staff member could be assigned from 7pm to 8pm.
 * Relative times are assumed when there is no event start time, and allocations are expressed as
 * minutes/hours before or after the event time (whatever it is)
 *
 * FIXME: There's a lot of conditionals to handle either the relative or absolute cases. It feels
 * like there should be two different components? In any case, we should have a better way to
 * handle things
 *
 */
export interface StaffAllocationsPanelProps {
  eventDuration: number;
  staffAllocations: PartialStaffAllocation[];
  staffTemplates: StaffTemplate[];
  relatedEventTime?: RelatedEventTime;
  isLocked?: boolean;
  onChange: (staffAllocations: PartialStaffAllocation[]) => void;
}

export interface StaffAllocationsPanelState {
  expandedStaffAllocation: number;
  editedStaffAllocationStartTime: string;
  editedStaffAllocationEndTime: string;
}

export default class StaffAllocationsPanel extends React.Component<
  StaffAllocationsPanelProps,
  StaffAllocationsPanelState
> {
  public readonly state: StaffAllocationsPanelState = {
    expandedStaffAllocation: -1,
    editedStaffAllocationStartTime: '',
    editedStaffAllocationEndTime: '',
  };

  constructor(props: StaffAllocationsPanelProps) {
    super(props);
  }

  public render() {
    return (
      <fieldset disabled={this.props.isLocked}>
        <div className="flex flex-col">
          <FormGroup>
            <span
              className={`inline-block mb-4 bg-orange-light p-2 text-base font-bold
text-grey-dark`}
            >
              {this.getEventInformation(this.props.eventDuration, this.props.relatedEventTime)}
            </span>
          </FormGroup>
          <FormGroup>
            <table className="StaffAllocationTable w-full text-left table-fixed mb-2">
              <thead>
                <tr className="text-teal-dark">
                  <th style={{ width: '15%' }}>Amount</th>
                  <th style={{ width: '25%' }}>Staff</th>
                  <th style={{ width: '25%' }}>Begin/End Time</th>
                  <th style={{ width: '25%' }}>Function</th>
                  <th style={{ width: '10%' }} />
                </tr>
              </thead>
              <tbody>
                {this.props.staffAllocations.map((sa, i) => (
                  <React.Fragment key={sa._id}>
                    <tr>
                      <td>
                        <input
                          value={sa.quantity}
                          onChange={e => this.handleStaffAllocationAmountChange(i, e)}
                        />
                      </td>
                      <td>
                        <Typeahead
                          size="sm"
                          value={sa.template ? sa.template.title : undefined}
                          onSelect={item => this.handleStaffAllocationStaffChange(i, item)}
                          items={this.props.staffTemplates.map(s => ({
                            key: s._id,
                            element: s.title,
                          }))}
                        />
                      </td>
                      {this.state.expandedStaffAllocation === i ? (
                        <td className="flex">
                          <input
                            style={{ width: '40%' }}
                            value={this.state.editedStaffAllocationStartTime}
                            onChange={this.handleStaffAllocationStartTimeChange}
                            onKeyPress={e => this.handleStaffAllocationStartTimeKeyPress(i, e)}
                            onBlur={e => this.handleStaffAllocationStartTimeBlur(i, e)}
                          />
                          <span className="text-center" style={{ width: '20%' }}>
                            to
                          </span>
                          <input
                            style={{ width: '40%' }}
                            value={this.state.editedStaffAllocationEndTime}
                            onChange={this.handleStaffAllocationEndTimeChange}
                            onKeyPress={e => this.handleStaffAllocationEndTimeKeyPress(i, e)}
                            onBlur={e => this.handleStaffAllocationEndTimeBlur(i, e)}
                          />
                        </td>
                      ) : (
                        <td>
                          <TextButton
                            className="text-teal-darker underline"
                            onClick={() => this.handleStaffAllocationTimeClick(i)}
                          >
                            {this.getStaffAllocationTimeRange(sa, this.props.relatedEventTime)}
                          </TextButton>
                        </td>
                      )}
                      <td>
                        <input
                          value={sa.activity}
                          onChange={e => this.handleStaffAllocationFunctionChange(i, e)}
                        />
                      </td>
                      <td>
                        <TextButton
                          className="border-none"
                          onClick={() => this.handleStaffAllocationCopyClick(i)}
                        >
                          <InlineIcon className="text-grey text-lg" name={'copy'} />
                        </TextButton>
                        <TextButton
                          className="border-none"
                          onClick={() => this.handleStaffAllocationDeleteClick(i)}
                        >
                          <InlineIcon className="text-red text-lg" name={'trash'} />
                        </TextButton>
                      </td>
                    </tr>
                    {this.state.expandedStaffAllocation === i && (
                      <tr>
                        <td colSpan={5}>
                          <StaffAllocationSlider
                            staffAllocation={sa}
                            relatedEventTime={this.props.relatedEventTime}
                            eventDuration={this.props.eventDuration}
                            onHideClick={this.handleStaffAllocationHideClick}
                            onChange={(start, end) =>
                              this.handleStaffAllocationTimeChange(i, start, end)
                            }
                          />
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
            <Button onClick={this.handleStaffAllocationAddClick} color="green">
              <div className="inline-flex items-center">
                <InlineIcon name={'plus'} className="mr-1 text-base" />
                <span>Add Staff</span>
              </div>
            </Button>
          </FormGroup>
        </div>
      </fieldset>
    );
  }

  private getEventInformation(duration: number, relatedEventTime?: RelatedEventTime): string {
    let eventInformationString = '';

    if (relatedEventTime) {
      let startTimeString = timeToString(
        relatedEventTime.eventStart,
        relatedEventTime.hourFormat,
        DEFAULT_TIMESTR_FORMAT
      );
      let endTime = addMinutesToTime(relatedEventTime.eventStart, duration);
      let endTimeString = timeToString(
        endTime,
        relatedEventTime.hourFormat,
        DEFAULT_TIMESTR_FORMAT
      );
      eventInformationString = `Event Time: ${startTimeString} - ${endTimeString}`;
    } else {
      return `Duration: ${minutesToTimeSpan(duration)}`;
    }
    return eventInformationString;
  }

  private getStaffAllocationTimeRange(
    sa: PartialStaffAllocation,
    relatedEventTime?: RelatedEventTime
  ): string {
    let startTime = this.getStaffAllocationStartTime(sa, relatedEventTime);
    let endTime = this.getStaffAllocationEndTime(sa, relatedEventTime);
    return `${startTime} to ${endTime}`;
  }

  private getStaffAllocationStartTime(
    staffAllocation: PartialStaffAllocation,
    relatedEventTime?: RelatedEventTime
  ): string {
    let timeString;
    if (relatedEventTime) {
      timeString = staffMinutesToTime(
        staffAllocation.startTime,
        relatedEventTime.eventStart,
        relatedEventTime.hourFormat
      );
    } else {
      timeString = staffMinutesToRelativeTime(staffAllocation.startTime);
    }

    return timeString;
  }

  private getStaffAllocationEndTime(
    staffAllocation: PartialStaffAllocation,
    relatedEventTime?: RelatedEventTime
  ): string {
    let timeString;

    if (relatedEventTime) {
      timeString = staffMinutesToTime(
        staffAllocation.startTime + staffAllocation.duration,
        relatedEventTime.eventStart,
        relatedEventTime.hourFormat
      );
    } else {
      timeString = staffMinutesToRelativeTime(staffAllocation.startTime + staffAllocation.duration);
    }

    return timeString;
  }

  private handleStaffAllocationTimeClick = (index: number) => {
    let staffAllocation = this.props.staffAllocations[index];
    if (!staffAllocation) return;

    // Expands the staff allocation time slider, and set the edited input values
    let editedStaffAllocationStartTime = this.getStaffAllocationStartTime(
      staffAllocation,
      this.props.relatedEventTime
    );
    let editedStaffAllocationEndTime = this.getStaffAllocationEndTime(
      staffAllocation,
      this.props.relatedEventTime
    );

    this.setState({
      expandedStaffAllocation: index,
      editedStaffAllocationStartTime,
      editedStaffAllocationEndTime,
    });
  };

  private handleStaffAllocationHideClick = () => {
    this.setState({
      expandedStaffAllocation: -1,
      editedStaffAllocationStartTime: '',
      editedStaffAllocationEndTime: '',
    });
  };

  private handleStaffAllocationTimeChange = (index: number, start: number, end: number) => {
    let staffAllocation = this.props.staffAllocations[index];
    if (!staffAllocation) return;

    staffAllocation.startTime = start;
    staffAllocation.duration = end - start;

    let editedStaffAllocationStartTime;
    let editedStaffAllocationEndTime;

    if (this.props.relatedEventTime) {
      let staffAllocationStartTime = addMinutesToTime(
        this.props.relatedEventTime.eventStart,
        start
      );
      let staffAllocationEndTime = addMinutesToTime(
        staffAllocationStartTime,
        staffAllocation.duration
      );

      editedStaffAllocationStartTime = timeToString(
        staffAllocationStartTime,
        this.props.relatedEventTime.hourFormat,
        DEFAULT_TIMESTR_FORMAT
      );

      editedStaffAllocationEndTime = timeToString(
        staffAllocationEndTime,
        this.props.relatedEventTime.hourFormat,
        DEFAULT_TIMESTR_FORMAT
      );
    } else {
      editedStaffAllocationStartTime = staffMinutesToRelativeTime(start);
      editedStaffAllocationEndTime = staffMinutesToRelativeTime(end);
    }

    this.setState({
      editedStaffAllocationStartTime,
      editedStaffAllocationEndTime,
    });

    this.props.onChange(this.props.staffAllocations);
  };

  private handleStaffAllocationAmountChange = (
    index: number,
    event: React.ChangeEvent<InputElement>
  ) => {
    let value = parseInt(event.target.value, 10);
    if (Number.isNaN(value)) return;

    let staffAllocation = this.props.staffAllocations[index];
    if (!staffAllocation) return;

    staffAllocation.quantity = value;
    this.props.onChange(this.props.staffAllocations);
  };
  private handleStaffAllocationStaffChange = (index: number, item: TypeaheadItem) => {
    let staffAllocation = this.props.staffAllocations[index];
    if (!staffAllocation) return;

    let staffTemplate = this.props.staffTemplates.find(st => st._id === item.key);
    if (!staffTemplate) return;

    staffAllocation.template = staffTemplate;
    this.props.onChange(this.props.staffAllocations);
  };

  private handleStaffAllocationStartTimeChange = (event: React.ChangeEvent<InputElement>) => {
    this.setState({ editedStaffAllocationStartTime: event.target.value });
  };
  private handleStaffAllocationEndTimeChange = (event: React.ChangeEvent<InputElement>) => {
    this.setState({ editedStaffAllocationEndTime: event.target.value });
  };

  private setStartTime(staffAllocation: PartialStaffAllocation, value: string) {
    try {
      let relativeStartTime;

      if (this.props.relatedEventTime) {
        let dayStartMinutes = timeToMinutes(this.props.relatedEventTime.dayStart);
        let eventStartTimeMinutes = timeToMinutes(
          this.props.relatedEventTime.eventStart,
          dayStartMinutes
        );
        let startTime = stringToTime(value);
        let startTimeMinutes = timeToMinutes(startTime, dayStartMinutes);
        relativeStartTime = startTimeMinutes - eventStartTimeMinutes;
      } else {
        relativeStartTime = relativeTimeToMinutes(value);
      }

      // We need to recalculate the duration so that the end time stays the same
      staffAllocation.duration += staffAllocation.startTime - relativeStartTime;
      staffAllocation.startTime = relativeStartTime;

      this.props.onChange(this.props.staffAllocations);
    } catch (_e) {
      // pass
    }
  }

  private setEndTime(staffAllocation: PartialStaffAllocation, value: string) {
    try {
      let relativeEndTime;

      if (this.props.relatedEventTime) {
        let dayStartMinutes = timeToMinutes(this.props.relatedEventTime.dayStart);
        let eventStartTimeMinutes = timeToMinutes(
          this.props.relatedEventTime.eventStart,
          dayStartMinutes
        );
        let endTime = stringToTime(value);
        let endTimeMinutes = timeToMinutes(endTime, dayStartMinutes);
        relativeEndTime = endTimeMinutes - eventStartTimeMinutes;
      } else {
        relativeEndTime = relativeTimeToMinutes(value);
      }

      staffAllocation.duration = relativeEndTime - staffAllocation.startTime;

      this.props.onChange(this.props.staffAllocations);
    } catch (_e) {
      // pass
    }
  }

  private handleStaffAllocationStartTimeBlur = (
    index: number,
    _event: React.FocusEvent<HTMLInputElement>
  ) => {
    let staffAllocation = this.props.staffAllocations[index];
    if (!staffAllocation) return;

    this.setStartTime(staffAllocation, this.state.editedStaffAllocationStartTime);
  };

  private handleStaffAllocationEndTimeBlur = (
    index: number,
    _event: React.FocusEvent<HTMLInputElement>
  ) => {
    let staffAllocation = this.props.staffAllocations[index];
    if (!staffAllocation) return;

    this.setEndTime(staffAllocation, this.state.editedStaffAllocationEndTime);
  };

  private handleStaffAllocationStartTimeKeyPress = (
    index: number,
    event: React.KeyboardEvent<HTMLInputElement>
  ) => {
    if (event.key !== 'Enter') return;

    let staffAllocation = this.props.staffAllocations[index];
    if (!staffAllocation) return;

    this.setStartTime(staffAllocation, this.state.editedStaffAllocationStartTime);
  };

  private handleStaffAllocationEndTimeKeyPress = (
    index: number,
    event: React.KeyboardEvent<HTMLInputElement>
  ) => {
    if (event.key !== 'Enter') return;

    let staffAllocation = this.props.staffAllocations[index];
    if (!staffAllocation) return;

    this.setEndTime(staffAllocation, this.state.editedStaffAllocationEndTime);
  };

  private handleStaffAllocationFunctionChange = (
    index: number,
    event: React.ChangeEvent<InputElement>
  ) => {
    let staffAllocation = this.props.staffAllocations[index];
    if (!staffAllocation) return;

    staffAllocation.activity = event.target.value;
    this.props.onChange(this.props.staffAllocations);
  };

  private handleStaffAllocationAddClick = async () => {
    if (this.props.isLocked) {
      await this.displayEditLockedToast();
    } else {
      // NOTE: We need to cast to `any` because there is no template assigned yet!
      this.props.staffAllocations.push({
        _id: new ObjectId().toHexString(),
        template: undefined,
        startTime: 0,
        duration: this.props.eventDuration,
        activity: '',
        quantity: 1,
      } as any);

      this.props.onChange(this.props.staffAllocations);
    }
  };

  private handleStaffAllocationCopyClick = async (index: number) => {
    if (this.props.isLocked) {
      await this.displayEditLockedToast();
    } else {
      if (index >= this.props.staffAllocations.length) return;

      let copiedStaffAllocation = {
        ...this.props.staffAllocations[index],
      };

      // ensure we assign a new ID, since they are used as a key
      copiedStaffAllocation._id = new ObjectId().toHexString();

      this.props.staffAllocations.push(copiedStaffAllocation);

      this.props.onChange(this.props.staffAllocations as StaffAllocation[]);
    }
  };

  private displayEditLockedToast = async () => {
    await toast.warning('Staff Allocations editing is locked');
  };
  private handleStaffAllocationDeleteClick = async (index: number) => {
    if (this.props.isLocked) {
      await this.displayEditLockedToast();
    } else {
      if (index >= this.props.staffAllocations.length) return;

      this.props.staffAllocations.splice(index, 1);

      this.props.onChange(this.props.staffAllocations);
    }
  };
}
